<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { Icon } from '@brighterly/lib-lumee-ui'

import { useOptionsStore } from '@shared/model'
import { LoaderBlockDomain } from '@shared/ui/loaders'
import { LoaderFullScreenDomain } from '@shared/ui/loaders'

import { useVoipRoomStore } from '../model/voip-room-store'
import { useLessonRoomStore } from '../model/store'
import { useIsPresentingLiveKit } from '../model/presenting-live-kit.hook'
import { useClassroomJoining } from '../model/joining.hook'
import { useConnectionWatchLiveKit } from '../model/connection-watch-live-kit.hook'
import ClassroomHeader from './ClassroomHeader.vue'
import ClassroomControlsBar from './ClassroomControlsBar.vue'
import LessonRoomGuidesPopup from './lesson-room/LessonRoomGuidesPopup.vue'
import LessonRoomChat from './lesson-room/LessonRoomChat.vue'
import LessonRoomSettingsDrawer from './lesson-room/LessonRoomSettingsDrawer.vue'
import LessonRoomPermissionsBlocked from './lesson-room/LessonRoomPermissionsBlocked.vue'

defineOptions({
  name: 'ClassroomLayout',
})

const optionsStore = useOptionsStore()
const lessonRoomStore = useLessonRoomStore()
const voipRoomStore = useVoipRoomStore()

const isPresenting = useIsPresentingLiveKit()
const { isJoining } = useClassroomJoining()

useConnectionWatchLiveKit()
const controlsRevealed = ref<boolean>(false)

// A fresh share starts hidden; ending a share restores the normal bar.
watch(isPresenting, (presenting) => {
  if (!presenting) controlsRevealed.value = false
})

const isControlsHidden = computed<boolean>(() => isPresenting.value && !controlsRevealed.value)
const isPermissionsPromptShown = computed<boolean>(() => lessonRoomStore.isPermissionsBlocked && !voipRoomStore.isAlternativeVoip)
const isJoiningLoaderShown = computed<boolean>(() => isJoining.value && !isPermissionsPromptShown.value)

const toggleControls = (): void => {
  controlsRevealed.value = !controlsRevealed.value
}
</script>

<template>
  <div
    v-if="optionsStore.hasData"
    :class="['classroom', {
      'classroom--presenting': isPresenting,
      'classroom--revealed': controlsRevealed,
    }]"
  >
    <ClassroomHeader class="classroom__header" />

    <main class="classroom__content">
      <slot />
    </main>

    <div
      :class="['classroom__controls', {
        'classroom__controls--presenting': isPresenting,
        'classroom__controls--hidden': isControlsHidden,
      }]"
    >
      <button
        v-if="isPresenting"
        class="classroom__controls-toggle"
        type="button"
        :aria-label="controlsRevealed ? 'Hide controls' : 'Show controls'"
        @click="toggleControls"
      >
        <Icon
          :name="controlsRevealed ? 'chevron-down' : 'chevron-up'"
          size="md"
          color="inherit"
        />
      </button>

      <ClassroomControlsBar />
    </div>

    <LoaderFullScreenDomain v-if="isJoiningLoaderShown" />

    <LessonRoomGuidesPopup v-if="lessonRoomStore.isGuideOpen" />
    <LessonRoomChat />
    <LessonRoomSettingsDrawer />
    <LessonRoomPermissionsBlocked v-if="isPermissionsPromptShown" />
  </div>
  <LoaderBlockDomain v-else />
</template>

<style scoped lang="scss" src="./ClassroomLayout.scss" />
